import { useEffect, useRef, useState } from "react";
import { ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { useCourtyard } from "@/context/CourtyardContext";
import { useLenisPause } from "@/components/invite/RsvpModal";
import { errMsg, http } from "@/lib/api";

const MAX_IN = 40 * 1024 * 1024;

async function prepare(file) {
  const heic = /hei[cf]$/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
  if (!file.type.startsWith("image/") && !heic) throw new Error("That doesn’t look like a photo. Please choose a JPG, PNG, WebP or HEIC image.");
  if (file.size > MAX_IN) throw new Error("That photo is larger than 40 MB. Please choose a smaller one.");
  let bmp;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new Error(
      heic
        ? "This browser can’t open HEIC photos. Try uploading from your iPhone’s Photos app (it converts automatically) or choose a JPG."
        : "We couldn’t read that image. Please try a JPG or PNG."
    );
  }
  const scale = Math.min(1, 2000 / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bmp.width * scale);
  canvas.height = Math.round(bmp.height * scale);
  canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.86));
  if (!blob) throw new Error("We couldn’t prepare that image. Please try another.");
  return blob;
}

export default function UploadDialog({ open, onOpenChange, onUploaded }) {
  const { rsvp } = useCourtyard();
  const input = useRef(null);
  const [blob, setBlob] = useState(null);
  const [preview, setPreview] = useState("");
  const [caption, setCaption] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  useLenisPause(open);

  useEffect(() => {
    if (!open) return undefined;
    setBlob(null);
    setPreview("");
    setCaption("");
    setName(rsvp?.name || "");
    setStatus("idle");
    setProgress(0);
    setError("");
    return undefined;
  }, [open, rsvp?.name]);

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const choose = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError("");
    setStatus("preparing");
    try {
      const b = await prepare(f);
      setBlob(b);
      setPreview(URL.createObjectURL(b));
      setStatus("idle");
    } catch (err) {
      setBlob(null);
      setPreview("");
      setError(err.message);
      setStatus("idle");
    }
  };

  const upload = async () => {
    if (!blob || status === "uploading") return;
    setStatus("uploading");
    setError("");
    setProgress(0);
    const fd = new FormData();
    fd.append("file", blob, "memory.jpg");
    fd.append("caption", caption);
    fd.append("display_name", name);
    try {
      const r = await http.post("/photos", fd, { onUploadProgress: (p) => p.total && setProgress(Math.round((p.loaded / p.total) * 100)) });
      setStatus("done");
      toast.success(r.data?.approved ? "Your memory is on the wall!" : "Uploaded! It’ll appear once the host approves it.");
      onUploaded();
      onOpenChange(false);
    } catch (err) {
      setStatus("idle");
      setError(errMsg(err, "Upload failed. Please try again."));
    }
  };

  const uploading = status === "uploading";
  return (
    <Dialog open={open} onOpenChange={(v) => !uploading && onOpenChange(v)}>
      <DialogContent data-lenis-prevent className="top-0 h-[100dvh] max-h-[100dvh] max-w-lg translate-y-0 overflow-y-auto rounded-none border-0 bg-[var(--ivory)] p-6 text-[var(--ink)] sm:top-[50%] sm:border-[3px] sm:border-[var(--navy)] sm:shadow-[8px_8px_0_#142344] sm:h-auto sm:max-h-[92dvh] sm:translate-y-[-50%] sm:rounded-[24px] sm:p-8" data-testid="upload-dialog">
        <div className="flex flex-col gap-5">
          <div>
            <p className="eyebrow">Add a memory</p>
            <DialogTitle className="font-poster mt-2 text-5xl leading-[0.95] text-[var(--navy)]">Share a <span className="font-serif-i text-[var(--pink)]">saboot</span></DialogTitle>
            <DialogDescription className="mt-1 text-[var(--ink)]">Everyone at the party can see it. Location data is removed automatically.</DialogDescription>
          </div>
          <input ref={input} type="file" accept="image/*,.heic,.heif" className="sr-only" onChange={choose} id="memory-file" data-testid="photo-file-input" />
          {preview ? (
            <div className="rounded-[8px] bg-white p-2 shadow-sm">
              <img src={preview} alt="Preview of your photo" className="mx-auto max-h-[38dvh] w-auto rounded-[4px] object-contain" data-testid="photo-preview" />
              <button type="button" className="mt-2 w-full min-h-[44px] text-sm font-semibold underline underline-offset-4" onClick={() => input.current?.click()} disabled={uploading}>Choose a different photo</button>
            </div>
          ) : (
            <button type="button" onClick={() => input.current?.click()} className="flex min-h-[180px] flex-col items-center justify-center gap-2 rounded-2xl border-[3px] border-dashed border-[var(--navy)] bg-white p-6 text-center hover:bg-white" data-testid="photo-choose-button">
              <ImagePlus className="h-8 w-8 text-[var(--brass-deep)]" aria-hidden="true" />
              <span className="font-semibold">{status === "preparing" ? "Preparing your photo…" : "Choose a photo from your device"}</span>
              <span className="text-xs text-[#3d5a51]">JPG, PNG, WebP or HEIC · large photos are resized for you</span>
            </button>
          )}
          <div>
            <label htmlFor="memory-caption" className="font-semibold">Caption <span className="font-normal text-[#3d5a51]">(optional)</span></label>
            <input id="memory-caption" className="field mt-2" maxLength={200} value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="photo-caption-input" />
          </div>
          <div>
            <label htmlFor="memory-name" className="font-semibold">Display name <span className="font-normal text-[#3d5a51]">(optional)</span></label>
            <input id="memory-name" className="field mt-2" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} data-testid="photo-name-input" />
          </div>
          {uploading && (
            <div role="status" aria-live="polite">
              <Progress value={progress} className="h-2 bg-[var(--ivory-2)] [&>div]:bg-[var(--marigold)]" />
              <p className="mt-1 text-sm">Uploading… {progress}%</p>
            </div>
          )}
          {error && <p role="alert" className="rounded-xl bg-[#fbe9e6] p-3 text-sm font-medium text-[#8a2525]" data-testid="upload-error">{error}</p>}
          <button type="button" className="btn-pink w-full" onClick={upload} disabled={!blob || uploading} data-testid="photo-submit-button">
            {uploading ? "Uploading…" : "Add to the wall"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
